import { memo, useCallback, useMemo, useState } from 'react';
import { playButtonClickSound } from '@wg/web2clientapi/sound';
import { ContextMenu, DivTooltip, Icon, TableBodyCell, TableRow, Tooltip, TooltipBody } from '@wg/wows-react-uikit';

import { root as ROOT_PATH } from '~/preloaded';
import { getPositionY } from '~/helpers/contextMenu';
import { floats, percent, thousands } from '~/helpers/formatting';
import { t } from '~/helpers/localization';
import { ROUTES_MAP } from '~/router/routes';

import { Clan, StatisticsValue } from '~/UIKit';

import ApplicationsNonClanMemberTableItemTooltip from './ApplicationsNonClanMemberTableItemTooltip';

import type { IProps as IContextMenuProps } from '@wg/wows-react-uikit/components/ContextMenu/ContextMenu';
import type { IconType } from '@wg/wows-react-uikit/components/Icon/Icon';
import type { NavigateFunction } from 'react-router-dom';
import type { IMemberApplication } from '~/Actions/ActionApplications';
import type { ClanType } from '~/Actions/ActionInvites';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';

interface ApplicationsNonClanMemberTableItemProps {
  navigate: NavigateFunction;
  application: IMemberApplication;
  currentAccount: ICurrentAccountState;
  hasActiveApplicationForClan: boolean;
  root?: string;
  sendApplication: (clan: ClanType) => void;
}

const ApplicationsNonClanMemberTableItem = ({
  navigate,
  application,
  currentAccount,
  hasActiveApplicationForClan,
  sendApplication,
}: ApplicationsNonClanMemberTableItemProps) => {
  const [isContextMenuActive, setIsContextMenuActive] = useState(false);
  const [contextMenuContext, setContextMenuContext] = useState<IContextMenuProps['context']>([]);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const clan = application.clan;
  const clanDisbanded = !clan.is_active;

  const isHiddenStatistics = application.is_hidden_statistics;
  const glyph = `status-${application.status}` as IconType;

  const clanProfileLink = useMemo(() => {
    return `${ROOT_PATH}${ROUTES_MAP.CLAN_PROFILE.replace(':clanId', String(clan.id))}`;
  }, [clan.id]);

  const buildContextMenuContext = useCallback((): IContextMenuProps['context'] => {
    return [
      {
        isNeedDivider: false,
        items: [
          {
            name: t('Профиль клана'),
            value: 'CLAN_PROFILE',
            disabled: false,
          },
          {
            name: t('Отправить заявку'),
            value: 'SEND_APPLICATION',
            disabled: hasActiveApplicationForClan,
            tooltip: hasActiveApplicationForClan ? t('Заявка уже отправлена') : undefined,
          },
        ],
      },
    ];
  }, [hasActiveApplicationForClan]);

  const onContextMenuStateChange = useCallback((active: boolean) => {
    setIsContextMenuActive(active);
  }, []);

  const onContextMenu = useCallback<React.MouseEventHandler<HTMLElement>>(
    (event) => {
      const context = buildContextMenuContext();
      const clientY = getPositionY(event, context);

      setContextMenuContext(context);
      setContextMenuPosition({
        x: event.clientX,
        y: clientY,
      });
    },
    [buildContextMenuContext],
  );

  const onContextMenuItemClick = useCallback<IContextMenuProps['onItemClick']>(
    (action) => {
      switch (action) {
        case 'CLAN_PROFILE':
          navigate(clanProfileLink);
          break;

        case 'SEND_APPLICATION':
          sendApplication(clan);
          break;

        default:
          break;
      }
    },
    [clan, clanProfileLink, navigate, sendApplication],
  );

  const onClanClick = useCallback(() => {
    if (!clan.is_active) {
      return;
    }
    void playButtonClickSound();
    navigate(clanProfileLink);
  }, [clan.is_active, clanProfileLink, navigate]);

  const tooltipStatus = useMemo(() => {
    if (isContextMenuActive) {
      return null;
    }

    return (
      <Tooltip>
        <TooltipBody>
          <ApplicationsNonClanMemberTableItemTooltip currentAccount={currentAccount} application={application} />
        </TooltipBody>
      </Tooltip>
    );
  }, [application, currentAccount, isContextMenuActive]);

  const contextMenu = useMemo(() => {
    if (!clan.is_active) {
      return null;
    }

    return (
      <ContextMenu
        context={contextMenuContext}
        position={contextMenuPosition}
        onItemClick={onContextMenuItemClick}
        onContextMenuStateChange={onContextMenuStateChange}
        isDemo={false}
      />
    );
  }, [clan.is_active, contextMenuContext, contextMenuPosition, onContextMenuItemClick, onContextMenuStateChange]);

  const statistics = application.statistics;

  return (
    <TableRow isHovering onContextMenu={clan.is_active ? onContextMenu : undefined} onClick={onClanClick}>
      {contextMenu}

      <TableBodyCell modify="basis">
        <Clan
          clanTag={clan.tag}
          clanColor={clan.color}
          clanName={clan.name}
          isDisbanded={clanDisbanded}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="small">{clan.members_count}</TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          clanDisbanded={clanDisbanded}
          value={thousands(statistics.abtl)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          clanDisbanded={clanDisbanded}
          value={percent(statistics.awb, undefined)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          clanDisbanded={clanDisbanded}
          value={thousands(statistics.aeb)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          clanDisbanded={clanDisbanded}
          value={thousands(statistics.admg)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          clanDisbanded={clanDisbanded}
          value={floats(statistics.afb, 2)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <DivTooltip tooltipBody={tooltipStatus}>
          <Icon glyph={glyph} />
        </DivTooltip>
      </TableBodyCell>
    </TableRow>
  );
};

export default memo(ApplicationsNonClanMemberTableItem);
